/**
 * Resolve the age-transition config (BABY/CHILD threshold + timing) for a
 * contract + group type from the contract's assigned tariff version. Replaces
 * the former org-level billing-settings source for the capacity allocation-create
 * auto-classification. Falls back to the canonical defaults (24 / 18 /
 * FOLLOWING_MONTH) when no tariff version resolves.
 */
import { useMemo } from 'react';
import { useTariff } from '@/hooks/useTariffs';
import type { AgeTransitionConfig } from '@/hooks/billing/settingsUtils';

export const AGE_TRANSITION_DEFAULTS: AgeTransitionConfig = {
  ageTransitionMonths: 24,
  ageTransitionMonthsSubsidised: 18,
  ageTransitionTiming: 'FOLLOWING_MONTH',
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function toTiming(value: unknown, fallback: string): string {
  return value === 'ON_BIRTHDAY' || value === 'FOLLOWING_MONTH' ? value : fallback;
}

/**
 * resolveTariffVersionId finds the tariff version id assigned to the given group
 * type on the contract (tolerating snake_case and camelCase shapes).
 */
export function resolveTariffVersionId(
  contract: unknown,
  groupType: string | undefined,
): string | undefined {
  const rec = asRecord(contract);
  if (!rec || !groupType) return undefined;
  const assignments = (rec.tariffVersions ?? rec.tariff_versions) as unknown;
  if (!Array.isArray(assignments)) return undefined;
  for (const a of assignments) {
    const ar = asRecord(a);
    if (!ar) continue;
    const gt = (ar.groupType ?? ar.group_type) as string | undefined;
    if (gt === groupType) {
      const id = (ar.tariffVersionId ?? ar.tariff_version_id) as string | undefined;
      if (id) return id;
    }
  }
  return undefined;
}

/**
 * extractAgeTransitionConfig reads the age-transition values off a tariff version
 * object (camelCase or snake_case), substituting defaults per field.
 */
export function extractAgeTransitionConfig(version: unknown): AgeTransitionConfig {
  const v = asRecord(version);
  if (!v) return AGE_TRANSITION_DEFAULTS;
  return {
    ageTransitionMonths: toNumber(
      v.ageTransitionMonths ?? v.age_transition_months,
      AGE_TRANSITION_DEFAULTS.ageTransitionMonths,
    ),
    ageTransitionMonthsSubsidised: toNumber(
      v.ageTransitionMonthsSubsidised ?? v.age_transition_months_subsidised,
      AGE_TRANSITION_DEFAULTS.ageTransitionMonthsSubsidised,
    ),
    ageTransitionTiming: toTiming(
      v.ageTransitionTiming ?? v.age_transition_timing,
      AGE_TRANSITION_DEFAULTS.ageTransitionTiming,
    ),
  };
}

export function useContractAgeTransitionConfig(
  contract: unknown,
  groupType: string | undefined,
): AgeTransitionConfig {
  const versionId = resolveTariffVersionId(contract, groupType);
  const { data: version } = useTariff(versionId ?? '');

  return useMemo(() => {
    if (!versionId || !version) {
      return AGE_TRANSITION_DEFAULTS;
    }
    return extractAgeTransitionConfig(version);
  }, [versionId, version]);
}
